import React, { useEffect } from "react";
import { useLoading } from "../../contexts/LoadingContext";

const ProgressCircle: React.FC<{ progress: number }> = ({ progress }) => {
  const circumference = 2 * Math.PI * 48;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative w-32 h-32">
      <div className="absolute inset-0 w-full h-full rounded-full border-4 border-gray-200" />

      <svg
        className="absolute inset-0 w-full h-full -rotate-90 transform"
        viewBox="0 0 100 100"
      >
        <circle
          className="stroke-primary transition-all duration-500 ease-in-out"
          strokeWidth="4"
          fill="transparent"
          r="48"
          cx="50"
          cy="50"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: strokeDashoffset,
          }}
        />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-semibold">{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

const LoadingOverlay: React.FC = () => {
  const { progress, isLoading, message, stopLoading } = useLoading();

  // Add/remove a class to the body when loading state changes
  useEffect(() => {
    if (isLoading) {
      // When loading starts, add a class to prevent scrolling
      document.body.classList.add("loading-active");

      // Also add a style to ensure all tutorial tooltips are hidden
      if (!document.getElementById("loading-override-style")) {
        const style = document.createElement("style");
        style.id = "loading-override-style";
        style.innerHTML = `
                    body.loading-active {
                        overflow: hidden;
                    }
                    
                    /* Hide all tooltips and any other high z-index elements when loading */
                    body.loading-active [style*="z-index"] {
                        visibility: hidden !important;
                    }
                    
                    /* Exception for our loading overlay */
                    body.loading-active #loading-overlay {
                        visibility: visible !important;
                    }
                    
                    /* Also hide any elements with position fixed/absolute */
                    body.loading-active .tutorial-target-highlight {
                        display: none !important;
                    }
                `;
        document.head.appendChild(style);
      }
    } else {
      // When loading ends, remove the class
      document.body.classList.remove("loading-active");
    }

    return () => {
      // Cleanup when component unmounts
      document.body.classList.remove("loading-active");
    };
  }, [isLoading]);

  if (!isLoading) return null;

  return (
    <div
      id="loading-overlay"
      className="fixed inset-0 bg-white z-[99999] flex items-center justify-center"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 99999,
      }}
    >
      <div className="flex flex-col items-center max-w-md mx-auto p-8">
        <ProgressCircle progress={progress} />
        <h3 className="mt-4 text-lg font-medium text-center">{message}</h3>
      </div>
    </div>
  );
};

export default LoadingOverlay;
